/**
 * VÉRAULT Member Suite — BalancesShelf (Wave 27.4 · Phase 4d).
 * ────────────────────────────────────────────────────────────────────
 * Summary view (Experience Total · Paid · Remaining · Upcoming Due) →
 * itemized detail on expand. Synced with itinerary cost data
 * (server-side aggregation in /essentials).
 *
 *   Named "Balances" — NEVER "Financials". Calm, reassuring,
 *   never bookkeeping software.
 *
 * Phase 4d polish:
 *   ▸ Per-item receipt PDF download (silent download icon).
 *   ▸ Invoice summary block (grouped totals by category).
 *   ▸ "Last updated" sync indicator (tiny mono caption with relative
 *     time + absolute hover hint).
 */
import React, { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, Check, Clock, Gift, Download, RefreshCw } from "lucide-react";

function fmtUSD(n, currency = "USD") {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(n);
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

const STATUS_META = {
  paid: { label: "Paid", Icon: Check, tone: "text-[hsl(var(--accent-sage))]" },
  pending: { label: "Pending", Icon: Clock, tone: "text-[hsl(var(--accent-gold))]" },
  deposit: { label: "Deposit", Icon: Check, tone: "text-[hsl(var(--accent-sage))]" },
  optional: { label: "Optional", Icon: Gift, tone: "text-[hsl(var(--foreground-muted))]" },
  included: { label: "Included", Icon: Check, tone: "text-[hsl(var(--foreground-muted))]" },
};

function StatusPill({ status }) {
  const meta = STATUS_META[(status || "").toLowerCase()] || STATUS_META.pending;
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] ${meta.tone}`}>
      <Icon className="size-3" strokeWidth={1.5} />
      {meta.label}
    </span>
  );
}

// Wave 27.4 · Phase 4d — Relative time formatter for "last updated".
function relativeTime(iso) {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const min = Math.floor(diff / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} ${min === 1 ? "minute" : "minutes"} ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} ${hr === 1 ? "hour" : "hours"} ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day} ${day === 1 ? "day" : "days"} ago`;
    if (day < 30) {
      const wk = Math.floor(day / 7);
      return `${wk} ${wk === 1 ? "week" : "weeks"} ago`;
    }
    const mo = Math.floor(day / 30);
    if (mo < 12) return `${mo} ${mo === 1 ? "month" : "months"} ago`;
    return `${Math.floor(mo / 12)} year ago`;
  } catch { return null; }
}

// Wave 27.4 · Phase 4d — Invoice summary block.
function InvoiceSummary({ groups, currency, testid }) {
  if (!groups?.length || groups.length <= 1) return null;
  return (
    <div className="mt-5 border-t border-[rgba(0,0,0,0.06)] pt-4" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-3">
        Invoice summary · {groups.length} categories
      </p>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        {groups.map((g) => {
          const settled = g.paid > 0 && g.remaining === 0;
          return (
            <li
              key={g.category}
              className="flex items-baseline justify-between gap-3"
              data-testid={`invoice-group-${g.category}`}
            >
              <span className="font-serif text-[14px] text-[hsl(var(--foreground-secondary))]">
                {g.label}
                <span className="ml-1.5 font-mono text-[10px] text-[hsl(var(--foreground-muted))]">· {g.count}</span>
              </span>
              <span className="flex items-baseline gap-2">
                <span className={`font-serif text-[14.5px] tabular-nums ${settled ? "text-[hsl(var(--accent-sage))]" : "text-[hsl(var(--foreground))]"}`}>
                  {fmtUSD(g.total, currency)}
                </span>
                {settled && <Check className="size-3 text-[hsl(var(--accent-sage))]" strokeWidth={2} />}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function BalancesShelf({ balances }) {
  const [expanded, setExpanded] = useState(false);
  if (!balances) return null;
  const {
    experience_total, paid, remaining, upcoming_due_date, currency,
    items = [], invoice_summary = [], last_updated_at,
  } = balances;
  // Silent if there's truly nothing to show
  if (!experience_total && !items.length) return null;

  const pct = experience_total > 0 ? Math.round((paid / experience_total) * 100) : 0;
  const lastUpdatedRel = relativeTime(last_updated_at);

  return (
    <section id="balances" data-testid="essentials-balances" className="scroll-mt-24">
      <header className="mb-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
            Balances
          </p>
          <h2 className="mt-1.5 font-serif text-[22px] sm:text-[26px] leading-tight tracking-tight">
            What's settled. What's still due.
          </h2>
        </div>
        {lastUpdatedRel && (
          <span
            className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]"
            title={`Last payment recorded: ${last_updated_at}`}
            data-testid="balances-last-updated"
          >
            <RefreshCw className="size-3" strokeWidth={1.5} />
            Last updated · {lastUpdatedRel}
          </span>
        )}
      </header>

      {/* Summary card */}
      <div className="rounded-md bg-white/65 backdrop-blur-[2px] border border-[rgba(0,0,0,0.06)] p-5 sm:p-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-5 sm:gap-x-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Experience total</p>
            <p className="mt-1.5 font-serif text-[22px] sm:text-[26px] tabular-nums tracking-tight" data-testid="balances-total">
              {fmtUSD(experience_total, currency)}
            </p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Paid</p>
            <p className="mt-1.5 font-serif text-[22px] sm:text-[26px] tabular-nums tracking-tight text-[hsl(var(--accent-sage))]" data-testid="balances-paid">
              {fmtUSD(paid, currency)}
            </p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Remaining</p>
            <p className="mt-1.5 font-serif text-[22px] sm:text-[26px] tabular-nums tracking-tight text-[hsl(var(--accent-gold))]" data-testid="balances-remaining">
              {fmtUSD(remaining, currency)}
            </p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Next due</p>
            <p className="mt-1.5 font-serif text-[18px] sm:text-[20px] tracking-tight" data-testid="balances-due-date">
              {upcoming_due_date ? fmtDate(upcoming_due_date) : "—"}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        {experience_total > 0 && (
          <div className="mt-5">
            <div className="h-[2px] w-full bg-[hsl(var(--border))] overflow-hidden rounded-full">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 1.0, ease: [0.22, 0.61, 0.36, 1] }}
                className="h-full bg-[hsl(var(--accent-sage))]"
                data-testid="balances-progress"
              />
            </div>
            <p className="mt-2 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
              {pct}% settled
            </p>
          </div>
        )}

        {items.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]"
            data-testid="balances-toggle"
          >
            {expanded ? "Hide itemized" : `View itemized · ${items.length}`}
            <ChevronDown className={`size-3 transition-transform ${expanded ? "rotate-180" : ""}`} strokeWidth={1.5} />
          </button>
        )}

        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
            className="mt-4 border-t border-[rgba(0,0,0,0.06)] pt-4"
          >
            <ul className="divide-y divide-[rgba(0,0,0,0.04)]" data-testid="balances-items-list">
              {items.map((it) => (
                <li key={it.id} className="py-3 flex items-start gap-4" data-testid={`balance-item-${it.id}`}>
                  <span className="flex-1 min-w-0">
                    <span className="block font-serif text-[15px] leading-tight text-[hsl(var(--foreground))]">
                      {it.label}
                    </span>
                    <span className="mt-1 flex items-center gap-3">
                      <StatusPill status={it.status} />
                      {it.due_date && it.status !== "paid" && (
                        <span className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
                          due {fmtDate(it.due_date)}
                        </span>
                      )}
                      {it.paid_date && it.status === "paid" && (
                        <span className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
                          {fmtDate(it.paid_date)}
                        </span>
                      )}
                      {it.invoice_number && (
                        <span className="font-mono text-[10px] text-[hsl(var(--foreground-muted))] tabular-nums">
                          {it.invoice_number}
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="flex items-center gap-3 flex-shrink-0">
                    <span className="font-serif text-[16px] tabular-nums text-[hsl(var(--foreground))]">
                      {fmtUSD(it.amount, it.currency || currency)}
                    </span>
                    {it.receipt_url && (
                      <a
                        href={it.receipt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Download receipt"
                        aria-label="Download receipt"
                        className="inline-flex items-center justify-center size-7 rounded-full border border-[hsl(var(--border))] text-[hsl(var(--foreground-muted))] hover:border-[hsl(var(--accent-gold))] hover:text-[hsl(var(--accent-gold))] transition-colors"
                        data-testid={`balance-receipt-${it.id}`}
                      >
                        <Download className="size-3" strokeWidth={1.5} />
                      </a>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Wave 27.4 · Phase 4d — invoice summary block */}
        <InvoiceSummary
          groups={invoice_summary}
          currency={currency}
          testid="balances-invoice-summary"
        />
      </div>
    </section>
  );
}
